// Genera el PDF del estado de cuenta de un cliente, para descargarlo o adjuntarlo a un mail.

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { ConfiguracionEstudio } from '@/services/configuracionService'
import type { DatosEstadoCuentaPDF } from '@/lib/pdfEstadoCuenta'
import { movimientosService } from '@/services/movimientosService'
import { armarFilasMovimientos, resumirPorPeriodo } from '@/lib/estado-cuenta-utils'
import { sortCronologico } from '@/lib/movimientos-utils'
import { formatearCUIT } from '@/lib/utils'

export type DatosEstadoCuentaSinEstudio = Omit<DatosEstadoCuentaPDF, 'estudio'>

/**
 * Renderiza `EstadoCuentaPDF` con los datos del estudio en el encabezado.
 * Carga `@react-pdf/renderer` recién al llamarse, para no sumarlo al bundle inicial.
 */
export const renderEstadoCuentaPDF = async (
  datos: DatosEstadoCuentaSinEstudio,
  config: ConfiguracionEstudio | null,
  logoUrl?: string,
): Promise<Blob> => {
  const [{ pdf }, { EstadoCuentaPDF }] = await Promise.all([
    import('@react-pdf/renderer'),
    import('@/lib/pdfEstadoCuenta'),
  ])

  const estudio = {
    nombre: config?.nombre ?? 'Estudio Contable',
    cuit: config?.cuit ?? '',
    direccion: config?.direccion ?? '',
    telefono: config?.telefono ?? '',
    email: config?.email ?? '',
    logoUrl,
  }

  return pdf(<EstadoCuentaPDF datos={{ ...datos, estudio }} />).toBlob()
}

interface GenerarEstadoCuentaArgs {
  supabase: SupabaseClient<Database>
  clienteId: string
  /** Organización activa: el cliente y sus movimientos se leen solo dentro de ella. */
  organizacionId: string
  configEstudio: ConfiguracionEstudio | null
  logoUrl?: string
}

/**
 * Devuelve en base64 el PDF del estado de cuenta del cliente, leído de la base en el momento.
 * Incluye todos sus movimientos, sin filtro de período ni de pendientes, con saldo inicial 0.
 * Lanza si no puede leer el cliente o sus movimientos, o si el cliente no es de la organización.
 */
export const generarEstadoCuentaBase64 = async ({
  supabase,
  clienteId,
  organizacionId,
  configEstudio,
  logoUrl,
}: GenerarEstadoCuentaArgs): Promise<string> => {
  const [{ data: cliente, error }, movimientos] = await Promise.all([
    supabase
      .from('clientes')
      .select('persona:personas(nombre, apellido, nombre_completo, cuit)')
      .eq('id', clienteId)
      .eq('organizacion_id', organizacionId)
      .single(),
    movimientosService.listar(supabase, organizacionId, { clienteId }),
  ])
  const persona = cliente?.persona
  if (error || !persona) throw new Error('No se pudo cargar el cliente para el estado de cuenta')

  const nombre =
    persona.nombre_completo ||
    (persona.apellido ? `${persona.nombre ?? ''} ${persona.apellido}` : persona.nombre) ||
    'Cliente'
  const { filas, totales: totalesMovimientos } = armarFilasMovimientos(
    [...movimientos].sort(sortCronologico),
  )
  const resumen = resumirPorPeriodo(movimientos)

  const blob = await renderEstadoCuentaPDF(
    {
      cliente: { nombre, cuit: persona.cuit ? formatearCUIT(String(persona.cuit)) : undefined },
      movimientos: filas,
      totalesMovimientos,
      periodos: resumen.filas,
      totales: resumen.totales,
    },
    configEstudio,
    logoUrl,
  )

  const bytes = new Uint8Array(await blob.arrayBuffer())
  let binario = ''
  for (let i = 0; i < bytes.length; i++) binario += String.fromCharCode(bytes[i])
  return btoa(binario)
}
